import { useState, useEffect } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import AccountSearchModal from './AccountSearchModal'

// Componente para selector de nivel de cuenta
const AccountLevelSelector = ({ parentId, levelIndex, selectedValue, clienteId, onSelect }) => {
  const { user } = useAuth()
  
  // La query se ejecuta si hay parentId y user, clienteId es opcional
  const { data: children, isLoading, error } = useQuery(
    ['accounts-children-level', parentId, clienteId],
    async () => {
      if (!parentId) return []
      let url = `/accounting/accounts/?is_active=true&parent=${parentId}&page_size=500`
      if (clienteId) {
        url += `&cliente_id=${clienteId}`
      }
      const response = await api.get(url)
      const results = response.data.results || response.data || []
      console.log('AccountLevelSelector - Respuesta de API:', {
        url,
        responseData: response.data,
        resultsCount: results?.length || 0,
        results: results
      })
      return results
    },
    { 
      enabled: !!parentId && !!user, // No requerir clienteId para habilitar la query
      staleTime: 30000, // Cache por 30 segundos
      cacheTime: 300000 // Mantener en cache por 5 minutos
    }
  )

  // Función para obtener el ID real de una cuenta
  const getAccountRealId = (account) => {
    if (typeof account.id === 'string' && account.id.startsWith('pers_')) {
      return account.cuenta_base_id || account.id
    }
    return account.id
  }

  // Si no hay parentId o user, no renderizar nada
  if (!parentId || !user) {
    console.log('AccountLevelSelector no renderizando - condiciones no cumplidas:', {
      parentId: !!parentId,
      user: !!user
    })
    return null
  }

  // Log para depuración
  console.log('AccountLevelSelector renderizando:', {
    parentId,
    levelIndex,
    clienteId,
    childrenCount: children?.length || 0,
    isLoading,
    error: error?.message,
    user: !!user,
    enabled: !!parentId && !!user
  })

  const activeChildren = children?.filter(acc => acc.is_active !== false) || []

  console.log('AccountLevelSelector - Datos procesados:', {
    parentId,
    childrenCount: children?.length || 0,
    activeChildrenCount: activeChildren.length,
    activeChildren: activeChildren.map(acc => ({ id: acc.id, code: acc.code, name: acc.name }))
  })

  // Renderizar el selector siempre, mostrando un mensaje si no hay hijos
  console.log('AccountLevelSelector - Renderizando componente visual:', {
    parentId,
    activeChildrenCount: activeChildren.length,
    isLoading,
    willRender: true
  })
  
  return (
    <div className="mb-2" style={{ marginLeft: `${(levelIndex + 1) * 20}px` }}>
      <div className="d-flex align-items-center gap-2">
        <Form.Select
          value={
            (() => {
              if (!selectedValue) {
                console.log('🟡 AccountLevelSelector - No hay selectedValue:', { selectedValue, parentId, levelIndex })
                return ''
              }
              
              const selectedValueStr = String(selectedValue)
              console.log('🟡 AccountLevelSelector - Buscando coincidencia:', {
                selectedValue: selectedValueStr,
                parentId,
                levelIndex,
                activeChildrenCount: activeChildren.length,
                childrenIds: activeChildren.map(acc => ({
                  id: String(acc.id),
                  realId: String(getAccountRealId(acc)),
                  cuenta_base_id: acc.cuenta_base_id ? String(acc.cuenta_base_id) : null,
                  code: acc.code,
                  name: acc.name
                }))
              })
              
              // Buscar la cuenta que coincida con el selectedValue
              // Puede ser que selectedValue sea un ID base o un ID de personalización
              const matchingAccount = activeChildren.find(acc => {
                const realId = String(getAccountRealId(acc))
                const accId = String(acc.id)
                const cuentaBaseId = acc.cuenta_base_id ? String(acc.cuenta_base_id) : null
                
                const matches = realId === selectedValueStr || 
                               accId === selectedValueStr ||
                               (cuentaBaseId && cuentaBaseId === selectedValueStr)
                
                if (matches) {
                  console.log('🟡 AccountLevelSelector - Coincidencia encontrada:', {
                    account: { id: accId, realId, code: acc.code, name: acc.name },
                    selectedValue: selectedValueStr
                  })
                }
                
                return matches
              })
              
              if (matchingAccount) {
                const result = String(getAccountRealId(matchingAccount))
                console.log('🟡 AccountLevelSelector - Valor seleccionado:', result)
                return result
              }
              
              // Si no se encuentra, usar el selectedValue directamente
              console.log('🟡 AccountLevelSelector - No se encontró coincidencia, usando selectedValue directo:', selectedValueStr)
              return selectedValueStr
            })()
          }
          onChange={(e) => {
            const childId = e.target.value
            if (childId) {
              const account = activeChildren.find(acc => {
                const realId = getAccountRealId(acc)
                return String(realId) === String(childId) || 
                       String(acc.id) === String(childId)
              })
              onSelect(childId, account || null)
            } else {
              onSelect(null, null)
            }
          }}
          disabled={isLoading}
          style={{ flex: 1 }}
        >
          <option value="">
            {isLoading 
              ? 'Cargando sub-cuentas...' 
              : activeChildren && activeChildren.length > 0
                ? 'Seleccionar sub-cuenta (opcional)...'
                : 'No hay sub-cuentas disponibles'}
          </option>
          {isLoading ? (
            <option disabled>Cargando...</option>
          ) : error ? (
            <option disabled>Error al cargar sub-cuentas</option>
          ) : activeChildren && activeChildren.length > 0 ? (
            activeChildren.map((account) => {
              const realId = getAccountRealId(account)
              return (
                <option key={account.id || realId} value={String(realId)}>
                  {account.code} - {account.name} {account.is_personalized ? '(Personalizado)' : ''}
                </option>
              )
            })
          ) : (
            <option disabled>No hay sub-cuentas en este nivel</option>
          )}
        </Form.Select>
        {selectedValue && activeChildren && activeChildren.length > 0 && (
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => {
              const account = activeChildren.find(acc => getAccountRealId(acc) == selectedValue)
              if (account) {
                onSelect(selectedValue, account)
              }
            }}
            title="Seleccionar esta cuenta como cuenta final"
          >
            ✓ Seleccionar
          </Button>
        )}
      </div>
      {!isLoading && activeChildren && activeChildren.length === 0 && !selectedValue && (
        <small className="text-muted d-block mt-1">
          Esta cuenta no tiene sub-cuentas. Puedes seleccionarla directamente.
        </small>
      )}
    </div>
  )
}

const AccountMapping = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [selectedMapping, setSelectedMapping] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [mappingToDelete, setMappingToDelete] = useState(null)
  
  // Filtros
  const { clientId: filterCliente, setClientId: setFilterCliente, selectDisabled } = useSyncedLocalClientId()
  const [filterTipo, setFilterTipo] = useState('')
  const [filterActivo, setFilterActivo] = useState('all')
  const [searchValue, setSearchValue] = useState('')
  
  // Sugerencias de mapeo
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false)
  const [sugerenciasMapeo, setSugerenciasMapeo] = useState([])
  const [cargandoSugerencias, setCargandoSugerencias] = useState(false)

  // Formulario
  const [formData, setFormData] = useState({
    isGeneral: false,
    cliente: '',
    clientes: [],
    cuenta_contable: '',
    tipo_mapeo: 'clave_sat',
    valor_mapeo: '',
    es_general: false,
    prioridad: 100,
    activo: true
  })

  // Selector jerárquico de cuentas
  const [selectedAccountPath, setSelectedAccountPath] = useState([])
  const [selectedAccountInfo, setSelectedAccountInfo] = useState(null)
  const [showAccountSearchModal, setShowAccountSearchModal] = useState(false)

  // Información del Catálogo SAT
  const [catalogoInfo, setCatalogoInfo] = useState(null)
  const [validatingCatalogo, setValidatingCatalogo] = useState(false)
  const [sugerenciasClave, setSugerenciasClave] = useState([])
  const [mostrarAutocompletar, setMostrarAutocompletar] = useState(false)
  const [buscandoSugerencias, setBuscandoSugerencias] = useState(false)
  const clienteContexto = formData.cliente || (formData.clientes && formData.clientes.length > 0 ? formData.clientes[0] : '')

  // Obtener mapeos de cuentas
  const { data: mappings, isLoading } = useQuery(
    ['account-mappings', filterCliente, filterTipo, filterActivo],
    async () => {
      const params = new URLSearchParams()
      if (filterCliente) params.append('cliente_id', filterCliente)
      if (filterTipo) params.append('tipo_mapeo', filterTipo)
      if (filterActivo !== 'all') params.append('activo', filterActivo === 'true')
      
      const response = await api.get(`/accounting/mapeos-cuenta/?${params.toString()}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener clientes
  const { data: clients } = useQuery(
    'clients-for-mapping',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener cuentas raíz (sin padre) para el selector jerárquico
  const { data: rootAccounts, isLoading: loadingRootAccounts } = useQuery(
    ['accounts-roots-mapping', clienteContexto],
    async () => {
      let url = '/accounting/accounts/?is_active=true&roots_only=true&page_size=500'
      if (clienteContexto) {
        url += `&cliente_id=${clienteContexto}`
      }
      const response = await api.get(url)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener hijos del último nivel seleccionado
  const lastSelectedAccountId = selectedAccountPath[selectedAccountPath.length - 1]
  const { data: _childAccounts } = useQuery(
    ['accounts-children-mapping', lastSelectedAccountId, clienteContexto],
    async () => {
      if (!lastSelectedAccountId) return []
      let url = `/accounting/accounts/?is_active=true&parent=${lastSelectedAccountId}&page_size=500`
      if (clienteContexto) {
        url += `&cliente_id=${clienteContexto}`
      }
      const response = await api.get(url)
      return response.data.results || response.data || []
    },
    { enabled: !!lastSelectedAccountId && !!user }
  )

  // Función para obtener hijos de un nivel específico

  // Función para cargar sugerencias de mapeo
  const cargarSugerencias = async () => {
    if (!filterCliente) {
      toast.info('Selecciona un cliente para ver sugerencias de mapeo')
      return
    }
    
    setCargandoSugerencias(true)
    try {
      const response = await api.get(
        `/accounting/mapeos-cuenta/sugerencias_mapeo/?cliente_id=${filterCliente}&limit=50`
      )
      const sugerencias = response.data.sugerencias || []
      setSugerenciasMapeo(sugerencias)
      setMostrarSugerencias(true)
      
      if (sugerencias.length === 0) {
        toast.info('No se encontraron claves SAT sin mapear para este cliente. Todas las claves ya tienen mapeo definido.')
      } else {
        toast.success(`Se encontraron ${sugerencias.length} claves SAT sin mapear`)
      }
    } catch (error) {
      console.error('Error al cargar sugerencias:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Error al cargar sugerencias'
      toast.error(errorMessage)
    } finally {
      setCargandoSugerencias(false)
    }
  }

  // Mutación para crear mapeo
  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/accounting/mapeos-cuenta/', data)
      return response.data
    },
    {
      onSuccess: (data) => {
        const createdCount = data?.created_count ?? 1
        const skippedCount = data?.skipped_count ?? 0
        if (createdCount > 1 || skippedCount > 0) {
          toast.success(`Mapeos creados: ${createdCount}. Omitidos por duplicado: ${skippedCount}.`)
        } else {
          toast.success('Mapeo creado correctamente')
        }
        // Ajustar filtros para mostrar el mapeo recién creado
        if (data.cliente && !filterCliente) {
          setFilterCliente(data.cliente.toString())
        }
        if (data.tipo_mapeo && !filterTipo) {
          setFilterTipo(data.tipo_mapeo)
        }
        // Invalidar queries y refrescar
        queryClient.invalidateQueries('account-mappings')
        handleCloseForm()
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Error al crear el mapeo'
        toast.error(errorMessage)
      }
    }
  )

  // Mutación para actualizar mapeo
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/accounting/mapeos-cuenta/${id}/`, data)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Mapeo actualizado correctamente')
        queryClient.invalidateQueries('account-mappings')
        handleCloseForm()
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Error al actualizar el mapeo'
        toast.error(errorMessage)
      }
    }
  )

  // Mutación para eliminar mapeo
  const deleteMutation = useMutation(
    async (id) => {
      await api.delete(`/accounting/mapeos-cuenta/${id}/`)
    },
    {
      onSuccess: () => {
        toast.success('Mapeo eliminado correctamente')
        queryClient.invalidateQueries('account-mappings')
        setShowDeleteConfirm(false)
        setMappingToDelete(null)
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Error al eliminar el mapeo'
        toast.error(errorMessage)
      }
    }
  )

  const handleOpenForm = (mapping = null) => {
    if (mapping) {
      setSelectedMapping(mapping)
      setFormData({
        isGeneral: !!mapping.es_general || !mapping.cliente,
        cliente: mapping.cliente,
        clientes: mapping.cliente ? [mapping.cliente] : [],
        cuenta_contable: mapping.cuenta_contable,
        tipo_mapeo: mapping.tipo_mapeo,
        valor_mapeo: mapping.valor_mapeo,
        es_general: !!mapping.es_general || !mapping.cliente,
        prioridad: mapping.prioridad,
        activo: mapping.activo
      })
      // TODO: Cargar ruta de la cuenta seleccionada si es necesario
      setSelectedAccountPath([])
      setSelectedAccountInfo(null)
    } else {
      setSelectedMapping(null)
      setFormData({
        isGeneral: false,
        cliente: '',
        clientes: [],
        cuenta_contable: '',
        tipo_mapeo: 'clave_sat',
        valor_mapeo: '',
        es_general: false,
        prioridad: 100,
        activo: true
      })
      setSelectedAccountPath([])
      setSelectedAccountInfo(null)
    }
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setSelectedMapping(null)
    setCatalogoInfo(null)
    setSelectedAccountPath([])
    setSelectedAccountInfo(null)
    setShowAccountSearchModal(false)
    setFormData({
      isGeneral: false,
      cliente: '',
      clientes: [],
      cuenta_contable: '',
      tipo_mapeo: 'clave_sat',
      valor_mapeo: '',
      es_general: false,
      prioridad: 100,
      activo: true
    })
  }

  // Función para obtener el ID real de una cuenta (maneja personalizaciones virtuales)
  const getAccountRealId = (account) => {
    // Si es una personalización virtual, usar cuenta_base_id o el ID base
    if (typeof account.id === 'string' && account.id.startsWith('pers_')) {
      return account.cuenta_base_id || account.id
    }
    return account.id
  }

  // Función para construir la ruta de una cuenta desde su ID
  const buildAccountPath = async (accountId, clienteId) => {
    if (!accountId) return []
    
    try {
      // Usar el endpoint de ruta completa
      let url = `/accounting/accounts/${accountId}/ruta_completa/`
      if (clienteId) {
        url += `?cliente_id=${clienteId}`
      }
      console.log('🔵 Llamando a ruta_completa:', url)
      const response = await api.get(url)
      const path = response.data.ruta || []
      console.log('🔵 Ruta recibida del endpoint:', path)
      return path
    } catch (error) {
      console.error('Error al construir ruta de cuenta:', error)
      // Fallback: intentar obtener la cuenta directamente y construir la ruta recursivamente
      try {
        let url = `/accounting/accounts/${accountId}/`
        if (clienteId) {
          url += `?cliente_id=${clienteId}`
        }
        const response = await api.get(url)
        const account = response.data
        console.log('🔵 Cuenta obtenida en fallback:', account)
        
        // Si tiene parent, construir ruta manualmente
        if (account.parent) {
          const parentId = typeof account.parent === 'object' 
            ? account.parent.id 
            : account.parent
          if (parentId) {
            const parentPath = await buildAccountPath(parentId, clienteId)
            const fullPath = [...parentPath, accountId]
            console.log('🔵 Ruta construida en fallback:', fullPath)
            return fullPath
          }
        }
        const singlePath = [accountId]
        console.log('🔵 Ruta sin parent (solo raíz):', singlePath)
        return singlePath
      } catch (fallbackError) {
        console.error('Error en fallback:', fallbackError)
        return []
      }
    }
  }

  // Handler cuando se selecciona desde el modal de búsqueda
  const handleAccountSearchSelect = async (cuenta) => {
    const realId = getAccountRealId(cuenta)
    if (!realId) return
    try {
      const path = await buildAccountPath(realId, clienteContexto)
      const normalizedPath = path.length > 0 ? path.map(id => String(id)) : [String(realId)]
      setSelectedAccountPath(normalizedPath)
      setSelectedAccountInfo({
        id: realId,
        code: cuenta.code,
        name: cuenta.name,
        is_personalized: cuenta.is_personalized || false
      })
      setFormData(prev => ({ ...prev, cuenta_contable: realId }))
      setShowAccountSearchModal(false)
    } catch (err) {
      console.error('Error al cargar ruta de cuenta:', err)
      toast.error('Error al cargar la cuenta seleccionada')
    }
  }

  // Función para seleccionar una cuenta en cualquier nivel
  const handleAccountSelect = (account, levelIndex) => {
    const realId = getAccountRealId(account)
    
    // Actualizar la ruta hasta este nivel
    const newPath = selectedAccountPath.slice(0, levelIndex + 1)
    if (newPath[newPath.length - 1] != realId) {
      newPath[newPath.length - 1] = realId
    }
    setSelectedAccountPath(newPath)
    
    // Guardar información de la cuenta seleccionada
    setSelectedAccountInfo({
      id: realId,
      code: account.code,
      name: account.name,
      is_personalized: account.is_personalized || false
    })
    
    // Actualizar formData con el ID de la cuenta
    setFormData({ ...formData, cuenta_contable: realId })
  }

  // Cargar ruta de cuenta cuando se edita un mapeo
  useEffect(() => {
    const loadAccountPath = async () => {
      if (selectedMapping && selectedMapping.cuenta_contable && clienteContexto) {
        console.log('🔵 Cargando ruta para edición:', {
          mappingId: selectedMapping.id,
          cuenta_contable: selectedMapping.cuenta_contable,
          cuenta_contable_type: typeof selectedMapping.cuenta_contable,
          cliente: clienteContexto,
          fullMapping: selectedMapping
        })
        
        const path = await buildAccountPath(selectedMapping.cuenta_contable, clienteContexto)
        console.log('🔵 Ruta cargada desde buildAccountPath:', {
          path,
          pathLength: path.length,
          cuenta_contable: selectedMapping.cuenta_contable,
          cliente: clienteContexto
        })
        
        // Normalizar los IDs del path a strings para asegurar comparaciones correctas
        const normalizedPath = path.map(id => String(id))
        console.log('🔵 Ruta normalizada:', {
          normalizedPath,
          normalizedPathLength: normalizedPath.length,
          firstElement: normalizedPath[0],
          lastElement: normalizedPath[normalizedPath.length - 1]
        })
        
        // Asegurar que la ruta no esté vacía
        if (normalizedPath.length === 0) {
          console.warn('🔵 Advertencia: La ruta está vacía, usando solo la cuenta contable')
          setSelectedAccountPath([String(selectedMapping.cuenta_contable)])
        } else {
          console.log('🔵 Estableciendo selectedAccountPath:', normalizedPath)
          setSelectedAccountPath(normalizedPath)
          
          // Si la ruta tiene más de un elemento, significa que hay una cuenta hija seleccionada
          // El último elemento es la cuenta final seleccionada
          if (normalizedPath.length > 1) {
            console.log('🔵 La ruta tiene múltiples niveles. Cuenta final:', normalizedPath[normalizedPath.length - 1])
          } else {
            console.log('🔵 La ruta solo tiene un nivel (cuenta raíz):', normalizedPath[0])
          }
        }
        
        // Obtener información de la cuenta seleccionada
        try {
          let url = `/accounting/accounts/${selectedMapping.cuenta_contable}/`
          if (clienteContexto) {
            url += `?cliente_id=${clienteContexto}`
          }
          const response = await api.get(url)
          const account = response.data
          console.log('🔵 Cuenta cargada desde API:', {
            id: account.id,
            code: account.code,
            name: account.name,
            parent_id: account.parent,
            parent: account.parent,
            parent_id_direct: account.parent_id,
            is_personalized: account.is_personalized || false,
            fullAccount: account
          })
          
          // Si la cuenta tiene parent_id, significa que es una cuenta hija
          // El endpoint ruta_completa debería devolver la ruta completa
          // Si no devuelve la ruta completa, puede ser un problema del endpoint
          if (account.parent_id || account.parent) {
            console.warn('🔵 ADVERTENCIA: La cuenta tiene parent_id pero ruta_completa solo devolvió un elemento:', {
              accountId: account.id,
              parentId: account.parent_id || account.parent,
              rutaReceived: normalizedPath,
              expectedRoute: normalizedPath.length > 1 ? 'debería tener múltiples elementos' : 'solo tiene un elemento'
            })
          }
          setSelectedAccountInfo({
            id: account.id,
            code: account.code,
            name: account.name,
            is_personalized: account.is_personalized || false
          })
        } catch (error) {
          console.error('🔵 Error al cargar cuenta:', error)
        }
      }
    }
    
    if (showForm && selectedMapping && clienteContexto) {
      loadAccountPath()
    } else if (showForm && !selectedMapping) {
      // Limpiar el path si no hay mapeo seleccionado
      setSelectedAccountPath([])
      setSelectedAccountInfo(null)
    }
    // buildAccountPath se redefine en cada render; incluirla provoca bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, selectedMapping, clienteContexto])

  // Validar clave SAT cuando cambia el valor y es tipo clave_sat
  useEffect(() => {
    const validateClaveSAT = async () => {
      if (formData.tipo_mapeo === 'clave_sat' && formData.valor_mapeo.trim() && clienteContexto) {
        setValidatingCatalogo(true)
        try {
          const response = await api.get(
            `/accounting/mapeos-cuenta/validar_clave_sat/?clave=${encodeURIComponent(formData.valor_mapeo.trim())}&cliente_id=${clienteContexto}`
          )
          setCatalogoInfo(response.data)
        } catch (error) {
          setCatalogoInfo({ existe: false, clave: formData.valor_mapeo })
        } finally {
          setValidatingCatalogo(false)
        }
      } else {
        setCatalogoInfo(null)
      }
    }

    // Debounce: esperar 500ms después de que el usuario deje de escribir
    const timeoutId = setTimeout(validateClaveSAT, 500)
    return () => clearTimeout(timeoutId)
  }, [formData.tipo_mapeo, formData.valor_mapeo, clienteContexto])

  // Buscar sugerencias de claves SAT para autocompletar
  useEffect(() => {
    const buscarSugerencias = async () => {
      if (formData.tipo_mapeo === 'clave_sat' && formData.valor_mapeo.trim().length >= 2 && clienteContexto) {
        setBuscandoSugerencias(true)
        try {
          const response = await api.get(
            `/accounting/mapeos-cuenta/buscar_claves_sat/?q=${encodeURIComponent(formData.valor_mapeo.trim())}&cliente_id=${clienteContexto}&limit=10`
          )
          setSugerenciasClave(response.data.results || [])
          setMostrarAutocompletar(true)
        } catch (error) {
          setSugerenciasClave([])
        } finally {
          setBuscandoSugerencias(false)
        }
        } else {
          setSugerenciasClave([])
          setMostrarAutocompletar(false)
        }
    }

    // Debounce: esperar 300ms para autocompletar
    const timeoutId = setTimeout(buscarSugerencias, 300)
    return () => clearTimeout(timeoutId)
  }, [formData.tipo_mapeo, formData.valor_mapeo, clienteContexto])

  const handleSubmit = (e) => {
    e.preventDefault()
    
    console.log('🟢 Enviando formulario:', {
      isEdit: !!selectedMapping,
      mappingId: selectedMapping?.id,
      formData,
      selectedAccountPath,
      selectedAccountInfo,
      cuenta_contable: formData.cuenta_contable,
      cuenta_contable_type: typeof formData.cuenta_contable
    })
    
    // Validaciones
    const selectedClientIds = Array.isArray(formData.clientes) ? formData.clientes.filter(Boolean) : []
    if (!formData.isGeneral && selectedClientIds.length === 0) {
      toast.error('Debes seleccionar al menos un cliente')
      return
    }
    if (!formData.cuenta_contable) {
      toast.error('Debes seleccionar una cuenta contable')
      return
    }
    if (!formData.valor_mapeo.trim()) {
      toast.error('Debes ingresar un valor de mapeo')
      return
    }

    if (selectedMapping) {
      updateMutation.mutate({
        id: selectedMapping.id,
        data: {
          ...formData,
          cliente: formData.isGeneral ? null : (selectedClientIds[0] || formData.cliente || null),
          es_general: !!formData.isGeneral
        }
      })
    } else {
      createMutation.mutate({
        ...formData,
        cliente: formData.isGeneral ? null : (selectedClientIds[0] || null),
        cliente_ids: formData.isGeneral ? [] : selectedClientIds,
        es_general: !!formData.isGeneral
      })
    }
  }

  const handleDelete = (mapping) => {
    setMappingToDelete(mapping)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = () => {
    if (mappingToDelete) {
      deleteMutation.mutate(mappingToDelete.id)
    }
  }

  // Filtrar mapeos por búsqueda
  const filteredMappings = mappings?.filter(mapping => {
    // Aplicar filtro de búsqueda si hay texto
    if (searchValue) {
      const searchLower = searchValue.toLowerCase()
      const matchesSearch = (
        (mapping.cliente_name || '').toLowerCase().includes(searchLower) ||
        (mapping.valor_mapeo || '').toLowerCase().includes(searchLower) ||
        (mapping.cuenta_contable_code || '').toLowerCase().includes(searchLower) ||
        (mapping.cuenta_contable_name || '').toLowerCase().includes(searchLower) ||
        (getTipoMapeoLabel(mapping.tipo_mapeo) || '').toLowerCase().includes(searchLower)
      )
      if (!matchesSearch) return false
    }
    return true
  }) || []

  // Obtener etiqueta del tipo de mapeo
  const getTipoMapeoLabel = (tipo) => {
    const labels = {
      'clave_sat': 'Clave SAT',
      'descripcion': 'Descripción',
      'tipo_gasto': 'Tipo de Gasto',
      'uso_cfdi': 'Uso CFDI',
      'forma_pago': 'Forma de pago',
      'metodo_pago': 'Método de pago'
    }
    return labels[tipo] || tipo
  }

  // Obtener nombre del cliente
  const getClientName = (client) => {
    return client.razon_social || client.business_name || `${client.nombre || ''} ${client.apellido_paterno || ''}`.trim()
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Mapeo de Cuentas</h3>
        <Button variant="primary" onClick={() => handleOpenForm()}>
          + Nuevo Mapeo
        </Button>
      </div>

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Cliente</Form.Label>
                <Form.Select
                  value={filterCliente}
                  onChange={(e) => {
                    setFilterCliente(e.target.value)
                    setMostrarSugerencias(false)
                    setSugerenciasMapeo([])
                  }}
                  disabled={selectDisabled}
                >
                  <option value="">Todos los clientes</option>
                  {clients && clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {getClientName(client)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Tipo de Mapeo</Form.Label>
                <Form.Select
                  value={filterTipo}
                  onChange={(e) => setFilterTipo(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="clave_sat">Clave SAT</option>
                  <option value="descripcion">Descripción</option>
                  <option value="tipo_gasto">Tipo de Gasto</option>
                  <option value="uso_cfdi">Uso CFDI</option>
                  <option value="forma_pago">Forma de pago</option>
                  <option value="metodo_pago">Método de pago</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={filterActivo}
                  onChange={(e) => setFilterActivo(e.target.value)}
                >
                  <option value="all">Todos</option>
                  <option value="true">Activos</option>
                  <option value="false">Inactivos</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Búsqueda</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Buscar por cliente, valor, cuenta..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="outline-info"
                onClick={cargarSugerencias}
                disabled={!filterCliente || cargandoSugerencias}
                className="w-100"
              >
                {cargandoSugerencias ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando...
                  </>
                ) : (
                  'Ver Sugerencias'
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Sugerencias de Mapeo */}
      {mostrarSugerencias && (
        <Card className="mb-4 border-info">
          <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
            <div>
              <strong>Sugerencias de Mapeo</strong>
              {sugerenciasMapeo.length > 0 && (
                <small className="ms-2">({sugerenciasMapeo.length} claves sin mapear)</small>
              )}
            </div>
            <Button
              variant="light"
              size="sm"
              onClick={() => {
                setMostrarSugerencias(false)
                setSugerenciasMapeo([])
              }}
            >
              ✕
            </Button>
          </Card.Header>
          <Card.Body>
            {sugerenciasMapeo.length === 0 ? (
              <Alert variant="info" className="mb-0">
                <strong>No hay sugerencias disponibles</strong>
                <br />
                <small>Todas las claves SAT de los conceptos procesados para este cliente ya tienen un mapeo definido, o no hay conceptos CFDI procesados.</small>
              </Alert>
            ) : (
              <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table striped hover size="sm">
                  <thead>
                    <tr>
                      <th>Clave</th>
                      <th>Descripción (Ejemplo)</th>
                      <th>Frecuencia</th>
                      <th>Catálogo SAT</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sugerenciasMapeo.map((sug, idx) => (
                      <tr key={idx}>
                        <td><strong>{sug.clave}</strong></td>
                        <td>
                          <small>{sug.descripcion_ejemplo || 'N/A'}</small>
                        </td>
                        <td>
                          <Badge bg="secondary">{sug.frecuencia}</Badge>
                        </td>
                        <td>
                          {sug.tiene_catalogo ? (
                            <Badge bg="success">✓ En catálogo</Badge>
                          ) : (
                            <Badge bg="warning">⚠ No encontrada</Badge>
                          )}
                          {sug.catalogo_info && (
                            <div className="mt-1">
                              <small className="text-muted">{sug.catalogo_info.descripcion}</small>
                            </div>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setFormData({
                                cliente: filterCliente,
                                cuenta_contable: '',
                                tipo_mapeo: 'clave_sat',
                                valor_mapeo: sug.clave,
                                prioridad: 100,
                                activo: true
                              })
                              setShowForm(true)
                              setMostrarSugerencias(false)
                            }}
                          >
                            Crear Mapeo
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : filteredMappings.length > 0 ? (
        <Card>
          <Card.Body>
            <div className="mb-3">
              <strong>Total de mapeos: {filteredMappings.length}</strong>
            </div>
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Tipo de Mapeo</th>
                  <th>Valor</th>
                  <th>Cuenta Contable</th>
                  <th>Ruta Jerárquica</th>
                  <th>Prioridad</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredMappings.map((mapping) => (
                  <tr key={mapping.id}>
                    <td><strong>{mapping.cliente_name || 'N/A'}</strong></td>
                    <td>
                      <Badge bg="info">{getTipoMapeoLabel(mapping.tipo_mapeo)}</Badge>
                    </td>
                    <td>
                      <div>
                        <strong>{mapping.valor_mapeo || 'N/A'}</strong>
                        {mapping.tipo_mapeo === 'clave_sat' && mapping.catalogo_sat_info && (
                          <div className="mt-1">
                            {mapping.catalogo_sat_info.existe ? (
                              <>
                                <Badge bg="success" className="me-1">✓ En catálogo</Badge>
                                <br />
                                <small className="text-muted">{mapping.catalogo_sat_info.descripcion}</small>
                              </>
                            ) : (
                              <Badge bg="warning">⚠ No encontrada</Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{mapping.cuenta_contable_code || 'N/A'}</strong>
                        <br />
                        <small className="text-muted">{mapping.cuenta_contable_name || ''}</small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {mapping.cuenta_contable_full_path || (
                            <>
                              {mapping.cuenta_contable_code || 'N/A'}
                              {mapping.cuenta_contable_name && ` - ${mapping.cuenta_contable_name}`}
                            </>
                          )}
                        </small>
                      </div>
                    </td>
                    <td>{mapping.prioridad}</td>
                    <td>
                      <Badge bg={mapping.activo ? 'success' : 'secondary'}>
                        {mapping.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenForm(mapping)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(mapping)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="info">
          {searchValue || filterCliente || filterTipo || filterActivo !== 'all' ? (
            <>
              <strong>No se encontraron mapeos con los filtros aplicados.</strong>
              <br />
              <small>
                Filtros activos: 
                {filterCliente && ` Cliente: ${clients?.find(c => c.id == filterCliente) ? getClientName(clients.find(c => c.id == filterCliente)) : filterCliente}`}
                {filterTipo && ` Tipo: ${getTipoMapeoLabel(filterTipo)}`}
                {filterActivo !== 'all' && ` Estado: ${filterActivo === 'true' ? 'Activos' : 'Inactivos'}`}
                {searchValue && ` Búsqueda: "${searchValue}"`}
              </small>
              <br />
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 mt-2"
                onClick={() => {
                  setFilterCliente('')
                  setFilterTipo('')
                  setFilterActivo('all')
                  setSearchValue('')
                }}
              >
                Limpiar filtros
              </Button>
            </>
          ) : (
            'No hay mapeos de cuentas configurados. Haz clic en "Nuevo Mapeo" para comenzar.'
          )}
        </Alert>
      )}

      {/* Modal de Creación/Edición */}
      <Modal show={showForm} onHide={handleCloseForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMapping ? 'Editar Mapeo de Cuenta' : 'Nuevo Mapeo de Cuenta'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    className="mb-2"
                    label="Mapeo general (aplica a clientes actuales y nuevos)"
                    checked={!!formData.isGeneral}
                    disabled={!!selectedMapping}
                    onChange={(e) => {
                      const isGeneral = e.target.checked
                      setFormData((prev) => ({
                        ...prev,
                        isGeneral,
                        es_general: isGeneral,
                        cliente: isGeneral ? '' : prev.cliente,
                        clientes: isGeneral ? [] : prev.clientes
                      }))
                    }}
                  />
                  <Form.Label>Cliente(s) {!formData.isGeneral && <span className="text-danger">*</span>}</Form.Label>
                  {selectedMapping ? (
                    <Form.Control
                      plaintext
                      readOnly
                      value={selectedMapping.cliente_name || 'N/A'}
                    />
                  ) : (
                    <>
                      {!formData.isGeneral && (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Text className="text-muted mb-0">
                              Se creará el mismo mapeo para cada cliente seleccionado.
                            </Form.Text>
                            {clients && clients.length > 0 && (
                              <div className="d-flex gap-2">
                                <Button
                                  type="button"
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => setFormData((prev) => ({
                                    ...prev,
                                    clientes: clients.map((c) => String(c.id)),
                                    cliente: String(clients[0]?.id || '')
                                  }))}
                                >
                                  Seleccionar todos
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => setFormData((prev) => ({
                                    ...prev,
                                    clientes: [],
                                    cliente: ''
                                  }))}
                                >
                                  Deseleccionar todos
                                </Button>
                              </div>
                            )}
                          </div>
                          <div
                            className="border rounded p-2"
                            style={{ maxHeight: '200px', overflowY: 'auto' }}
                          >
                            <Row>
                              {clients && (() => {
                                const mid = Math.ceil(clients.length / 2)
                                const left = clients.slice(0, mid)
                                const right = clients.slice(mid)
                                const isChecked = (id) => (formData.clientes || []).includes(String(id))
                                const toggleClient = (id, checked) => {
                                  const idStr = String(id)
                                  const current = (formData.clientes || []).map(String)
                                  const next = checked
                                    ? Array.from(new Set([...current, idStr]))
                                    : current.filter((x) => x !== idStr)
                                  setFormData((prev) => ({
                                    ...prev,
                                    clientes: next,
                                    cliente: next[0] || ''
                                  }))
                                }
                                return (
                                  <>
                                    <Col md={6}>
                                      {left.map((client) => (
                                        <Form.Check
                                          key={client.id}
                                          type="checkbox"
                                          id={`mapeo-cliente-${client.id}`}
                                          label={getClientName(client)}
                                          checked={isChecked(client.id)}
                                          onChange={(e) => toggleClient(client.id, e.target.checked)}
                                          className="mb-1"
                                        />
                                      ))}
                                    </Col>
                                    <Col md={6}>
                                      {right.map((client) => (
                                        <Form.Check
                                          key={client.id}
                                          type="checkbox"
                                          id={`mapeo-cliente-${client.id}`}
                                          label={getClientName(client)}
                                          checked={isChecked(client.id)}
                                          onChange={(e) => toggleClient(client.id, e.target.checked)}
                                          className="mb-1"
                                        />
                                      ))}
                                    </Col>
                                  </>
                                )
                              })()}
                            </Row>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  <Form.Text className="text-muted">
                    {selectedMapping
                      ? 'No se puede cambiar clientes en un mapeo existente'
                      : formData.isGeneral
                        ? 'Mapeo general activo: se aplicará a clientes actuales y futuros.'
                        : 'Selecciona uno o varios clientes para aplicar el mismo mapeo.'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Cuenta Contable <span className="text-danger">*</span></Form.Label>
                  
                  {/* Selector jerárquico de cuentas */}
                  {!clienteContexto && !formData.isGeneral ? (
                    <Alert variant="info" className="py-2 mb-0">
                      <small>Selecciona al menos un cliente primero para ver sus cuentas contables</small>
                    </Alert>
                  ) : (
                    <div>
                      {/* Nivel 0: Cuentas raíz */}
                      <Form.Select
                        value={
                          (() => {
                            if (!selectedAccountPath[0] || !rootAccounts) return ''
                            // Buscar la cuenta en rootAccounts que coincida con el ID del path
                            const matchingAccount = rootAccounts.find(acc => {
                              const realId = getAccountRealId(acc)
                              return String(realId) === String(selectedAccountPath[0]) || 
                                     String(acc.id) === String(selectedAccountPath[0])
                            })
                            return matchingAccount ? String(getAccountRealId(matchingAccount)) : String(selectedAccountPath[0])
                          })()
                        }
                        onChange={(e) => {
                          const rootId = e.target.value
                          console.log('🔵 Selección de cuenta raíz:', { 
                            rootId, 
                            rootAccountsCount: rootAccounts?.length,
                            currentPath: selectedAccountPath 
                          })
                          if (rootId) {
                            const account = rootAccounts?.find(acc => getAccountRealId(acc) == rootId)
                            console.log('🔵 Cuenta encontrada:', account ? { id: account.id, code: account.code, name: account.name } : 'No encontrada')
                            if (account) {
                              // Si solo cambiamos la raíz y ya había una ruta, resetear a solo la raíz
                              setSelectedAccountPath([rootId])
                              console.log('🔵 selectedAccountPath actualizado a:', [rootId])
                              // Luego actualizar la información de la cuenta
                              const realId = getAccountRealId(account)
                              setSelectedAccountInfo({
                                id: realId,
                                code: account.code,
                                name: account.name,
                                is_personalized: account.is_personalized || false
                              })
                              setFormData({ ...formData, cuenta_contable: realId })
                            } else {
                              setSelectedAccountPath([rootId])
                            }
                          } else {
                            setSelectedAccountPath([])
                            setSelectedAccountInfo(null)
                            setFormData({ ...formData, cuenta_contable: '' })
                          }
                        }}
                        required
                        disabled={loadingRootAccounts}
                        className="mb-2"
                      >
                        <option value="">Seleccionar cuenta raíz...</option>
                        {loadingRootAccounts ? (
                          <option disabled>Cargando...</option>
                        ) : rootAccounts && rootAccounts.length > 0 ? (
                          rootAccounts.map((account) => {
                            const realId = getAccountRealId(account)
                            return (
                              <option key={account.id || realId} value={realId}>
                                {account.code} - {account.name} {account.is_personalized ? '(Personalizado)' : ''}
                              </option>
                            )
                          })
                        ) : (
                          <option value="" disabled>No hay cuentas disponibles</option>
                        )}
                      </Form.Select>

                      {/* Niveles adicionales (hijos, nietos, etc.) */}
                      {/* Renderizar selectores para cada nivel de la jerarquía */}
                      {(() => {
                        const shouldRender = selectedAccountPath.length > 0 && clienteContexto
                        console.log('🔍 Condición de renderizado del contenedor:', {
                          selectedAccountPathLength: selectedAccountPath.length,
                          selectedAccountPath,
                          clienteId: clienteContexto,
                          shouldRender
                        })
                        return shouldRender
                      })() && (
                        <div className="mt-2" style={{ border: '2px solid #0d6efd', padding: '10px', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                          <small className="text-muted d-block mb-2"><strong>Sub-cuentas disponibles:</strong></small>
                          {/* Renderizar un selector para mostrar los hijos de cada nivel seleccionado */}
                          {/* Por ejemplo: si selectedAccountPath = [rootId], renderizar selector para hijos de rootId */}
                          {/* Si selectedAccountPath = [rootId, childId], renderizar selector para hijos de childId */}
                          {selectedAccountPath.map((parentId, levelIndex) => {
                            // Este selector muestra los hijos del nivel actual (parentId)
                            const nextLevelIndex = levelIndex + 1
                            const nextLevelValue = selectedAccountPath[nextLevelIndex] 
                              ? String(selectedAccountPath[nextLevelIndex]) 
                              : null
                            
                            // Normalizar parentId a string para consistencia
                            const normalizedParentId = String(parentId)
                            
                            // Log para depuración
                            console.log('🎯 Renderizando AccountLevelSelector para nivel:', {
                              levelIndex,
                              parentId: normalizedParentId,
                              nextLevelIndex,
                              nextLevelValue,
                              selectedAccountPathLength: selectedAccountPath.length,
                              selectedAccountPath,
                              clienteId: clienteContexto,
                              willRender: true
                            })
                            
                            return (
                              <AccountLevelSelector
                                key={`level-${levelIndex + 1}-${normalizedParentId}-${clienteContexto || 'no-client'}`}
                                parentId={normalizedParentId}
                                levelIndex={levelIndex}
                                selectedValue={nextLevelValue}
                                clienteId={clienteContexto}
                                onSelect={(value, account) => {
                                  console.log('AccountLevelSelector onSelect llamado:', { 
                                    value, 
                                    account: account ? { id: account.id, code: account.code, name: account.name } : null, 
                                    levelIndex,
                                    currentPath: selectedAccountPath
                                  })
                                  if (value) {
                                    // Agregar el nuevo nivel al path
                                    // Si selectedAccountPath = [rootId] y seleccionamos childId,
                                    // el nuevo path debe ser [rootId, childId]
                                    const newPath = [...selectedAccountPath.slice(0, nextLevelIndex), value]
                                    console.log('Nuevo path después de selección:', {
                                      oldPath: selectedAccountPath,
                                      newPath,
                                      nextLevelIndex,
                                      value,
                                      account
                                    })
                                    setSelectedAccountPath(newPath)
                                    
                                    // Actualizar la información de la cuenta seleccionada directamente
                                    // sin llamar a handleAccountSelect para evitar conflicto
                                    if (account) {
                                      const realId = getAccountRealId(account)
                                      console.log('🟢 Actualizando cuenta seleccionada desde AccountLevelSelector:', {
                                        account: { id: account.id, code: account.code, name: account.name },
                                        realId,
                                        newPath,
                                        oldFormDataCuentaContable: formData.cuenta_contable,
                                        willUpdateTo: realId
                                      })
                                      setSelectedAccountInfo({
                                        id: realId,
                                        code: account.code,
                                        name: account.name,
                                        is_personalized: account.is_personalized || false
                                      })
                                      const updatedFormData = { ...formData, cuenta_contable: realId }
                                      console.log('🟢 Actualizando formData.cuenta_contable:', {
                                        from: formData.cuenta_contable,
                                        to: realId,
                                        newFormData: updatedFormData
                                      })
                                      setFormData(updatedFormData)
                                    }
                                  } else {
                                    // Eliminar este nivel y todos los posteriores
                                    const newPath = selectedAccountPath.slice(0, nextLevelIndex)
                                    console.log('Nuevo path después de deselección:', newPath)
                                    setSelectedAccountPath(newPath)
                                    if (newPath.length > 0) {
                                      // Mantener la selección del nivel anterior
                                      if (levelIndex === 0) {
                                        const prevAccount = rootAccounts?.find(acc => getAccountRealId(acc) == newPath[0])
                                        if (prevAccount) {
                                          handleAccountSelect(prevAccount, 0)
                                        }
                                      }
                                    } else {
                                      setSelectedAccountInfo(null)
                                      setFormData({ ...formData, cuenta_contable: '' })
                                    }
                                  }
                                }}
                              />
                            )
                          })}
                        </div>
                      )}
                      
                      {/* Botón para confirmar selección de cuenta raíz */}
                      {selectedAccountPath[0] && !selectedAccountPath[1] && (
                        <div className="mb-2">
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => {
                              const rootId = selectedAccountPath[0]
                              const account = rootAccounts?.find(acc => getAccountRealId(acc) == rootId)
                              if (account) {
                                handleAccountSelect(account, 0)
                              }
                            }}
                          >
                            ✓ Seleccionar esta cuenta raíz
                          </Button>
                        </div>
                      )}

                      <div className="mt-2">
                        <small className="text-muted me-2">O bien:</small>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setShowAccountSearchModal(true)}
                        >
                          <i className="bi bi-search me-1"></i> Buscar cuenta...
                        </Button>
                      </div>

                      {/* Información de la cuenta seleccionada */}
                      {selectedAccountInfo && (
                        <Alert variant="success" className="py-2 mt-2 mb-0">
                          <strong>Cuenta seleccionada:</strong> {selectedAccountInfo.code} - {selectedAccountInfo.name}
                          {selectedAccountInfo.is_personalized && (
                            <Badge bg="info" className="ms-2">Personalizado</Badge>
                          )}
                        </Alert>
                      )}

                      {!loadingRootAccounts && rootAccounts && rootAccounts.length === 0 && (
                        <Form.Text className="text-danger">
                          No hay cuentas contables activas. Crea cuentas en el Plan de Cuentas primero.
                        </Form.Text>
                      )}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Mapeo <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.tipo_mapeo}
                    onChange={(e) => {
                      // Limpiar valor_mapeo cuando se cambia el tipo de mapeo
                      setFormData({ ...formData, tipo_mapeo: e.target.value, valor_mapeo: '' })
                      // Limpiar información del catálogo si se cambia el tipo
                      if (e.target.value !== 'clave_sat') {
                        setCatalogoInfo(null)
                        setSugerenciasClave([])
                        setMostrarAutocompletar(false)
                      }
                    }}
                    required
                  >
                    <option value="clave_sat">Clave SAT</option>
                    <option value="descripcion">Descripción</option>
                    <option value="tipo_gasto">Tipo de Gasto</option>
                    <option value="uso_cfdi">Uso CFDI</option>
                    <option value="forma_pago">Forma de pago</option>
                    <option value="metodo_pago">Método de pago</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {formData.tipo_mapeo === 'clave_sat' && 'Mapeo por clave de producto/servicio del SAT'}
                    {formData.tipo_mapeo === 'descripcion' && 'Mapeo por texto en la descripción del concepto'}
                    {formData.tipo_mapeo === 'tipo_gasto' && 'Mapeo por tipo de comprobante (I, E, P, etc.)'}
                    {formData.tipo_mapeo === 'uso_cfdi' && 'Mapeo por uso CFDI del receptor (G01, G02, G03, etc.)'}
                    {formData.tipo_mapeo === 'forma_pago' && 'Mapeo por forma de pago del CFDI (01 Efectivo, 03 Transferencia, etc.)'}
                    {formData.tipo_mapeo === 'metodo_pago' && 'Mapeo por método de pago del CFDI (PUE o PPD)'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" style={{ position: 'relative' }}>
                  <Form.Label>Valor de Mapeo <span className="text-danger">*</span></Form.Label>
                  <div style={{ position: 'relative' }}>
                    {/* Selector para tipo_gasto */}
                    {formData.tipo_mapeo === 'tipo_gasto' ? (
                      <Form.Select
                        value={formData.valor_mapeo}
                        onChange={(e) => setFormData({ ...formData, valor_mapeo: e.target.value })}
                        required
                      >
                        <option value="">Seleccionar tipo de comprobante...</option>
                        <option value="I">I - Ingreso</option>
                        <option value="E">E - Egreso</option>
                        <option value="P">P - Pago</option>
                        <option value="N">N - Nómina</option>
                        <option value="T">T - Traslado</option>
                      </Form.Select>
                    ) : formData.tipo_mapeo === 'uso_cfdi' ? (
                      <Form.Select
                        value={formData.valor_mapeo}
                        onChange={(e) => setFormData({ ...formData, valor_mapeo: e.target.value })}
                        required
                      >
                        <option value="">Seleccionar uso CFDI...</option>
                        <option value="G01">G01 - Adquisición de mercancías</option>
                        <option value="G02">G02 - Devoluciones, descuentos o bonificaciones</option>
                        <option value="G03">G03 - Gastos en general</option>
                        <option value="I01">I01 - Construcciones</option>
                        <option value="I02">I02 - Mobiliario y equipo de oficina por inversiones</option>
                        <option value="I03">I03 - Equipo de transporte</option>
                        <option value="I04">I04 - Equipo de computo y accesorios</option>
                        <option value="I05">I05 - Dados, troqueles, moldes, matrices y herramental</option>
                        <option value="I06">I06 - Comunicaciones telefónicas</option>
                        <option value="I07">I07 - Comunicaciones satelitales</option>
                        <option value="I08">I08 - Otra maquinaria y equipo</option>
                        <option value="D01">D01 - Honorarios médicos, dentales y gastos hospitalarios</option>
                        <option value="D02">D02 - Gastos médicos por incapacidad o discapacidad</option>
                        <option value="D03">D03 - Gastos funerales</option>
                        <option value="D04">D04 - Donativos</option>
                        <option value="D05">D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)</option>
                        <option value="D06">D06 - Aportaciones voluntarias al SAR</option>
                        <option value="D07">D07 - Primas por seguros de gastos médicos</option>
                        <option value="D08">D08 - Gastos de transportación escolar obligatoria</option>
                        <option value="D09">D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</option>
                        <option value="D10">D10 - Pagos por servicios educativos (colegiaturas)</option>
                        <option value="S01">S01 - Sin efectos fiscales</option>
                        <option value="CP01">CP01 - Pagos</option>
                        <option value="CN01">CN01 - Nómina</option>
                      </Form.Select>
                    ) : formData.tipo_mapeo === 'forma_pago' ? (
                      <Form.Select
                        value={formData.valor_mapeo}
                        onChange={(e) => setFormData({ ...formData, valor_mapeo: e.target.value })}
                        required
                      >
                        <option value="">Seleccionar forma de pago...</option>
                        <option value="01">01 - Efectivo</option>
                        <option value="02">02 - Cheque nominativo</option>
                        <option value="03">03 - Transferencia electrónica</option>
                        <option value="04">04 - Tarjeta de crédito</option>
                        <option value="05">05 - Monedero electrónico</option>
                        <option value="06">06 - Dinero electrónico</option>
                        <option value="08">08 - Vales de despensa</option>
                        <option value="12">12 - Dación en pago</option>
                        <option value="13">13 - Pago por subrogación</option>
                        <option value="14">14 - Pago por consignación</option>
                        <option value="15">15 - Condonación</option>
                        <option value="17">17 - Compensación</option>
                        <option value="23">23 - Novación</option>
                        <option value="24">24 - Confusión</option>
                        <option value="25">25 - Remisión de deuda</option>
                        <option value="26">26 - Prescripción o caducidad</option>
                        <option value="27">27 - A satisfacción del acreedor</option>
                        <option value="28">28 - Tarjeta de débito</option>
                        <option value="29">29 - Tarjeta de servicios</option>
                        <option value="30">30 - Aplicación de anticipos</option>
                        <option value="31">31 - Intermediario pagos</option>
                        <option value="99">99 - Por definir</option>
                      </Form.Select>
                    ) : formData.tipo_mapeo === 'metodo_pago' ? (
                      <Form.Select
                        value={formData.valor_mapeo}
                        onChange={(e) => setFormData({ ...formData, valor_mapeo: e.target.value })}
                        required
                      >
                        <option value="">Seleccionar método de pago...</option>
                        <option value="PUE">PUE - Pago en una sola exhibición</option>
                        <option value="PPD">PPD - Pago en parcialidades o diferido</option>
                      </Form.Select>
                    ) : (
                      <Form.Control
                        type="text"
                        value={formData.valor_mapeo}
                        onChange={(e) => {
                          setFormData({ ...formData, valor_mapeo: e.target.value })
                          setMostrarAutocompletar(true)
                        }}
                        onFocus={() => {
                          if (formData.tipo_mapeo === 'clave_sat' && sugerenciasClave.length > 0) {
                            setMostrarAutocompletar(true)
                          }
                        }}
                        onBlur={() => {
                          // Delay para permitir click en sugerencias
                          setTimeout(() => setMostrarAutocompletar(false), 200)
                        }}
                        placeholder={
                          formData.tipo_mapeo === 'clave_sat' ? 'Ej: 01010101 o buscar por descripción...' :
                          formData.tipo_mapeo === 'descripcion' ? 'Ej: gasolina, papelería, viáticos...' :
                          formData.tipo_mapeo === 'forma_pago' ? 'Seleccione forma de pago...' :
                          ''
                        }
                        required
                      />
                    )}
                    
                    {/* Autocompletar sugerencias */}
                    {formData.tipo_mapeo === 'clave_sat' && mostrarAutocompletar && sugerenciasClave.length > 0 && (
                      <div 
                        className="list-group"
                        style={{
                          position: 'absolute',
                          zIndex: 1000,
                          width: '100%',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          marginTop: '2px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}
                      >
                        {sugerenciasClave.map((sug, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setFormData({ ...formData, valor_mapeo: sug.clave })
                              setMostrarAutocompletar(false)
                            }}
                            style={{ cursor: 'pointer', textAlign: 'left' }}
                          >
                            <div>
                              <strong>{sug.clave}</strong>
                              <br />
                              <small className="text-muted">{sug.descripcion}</small>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {buscandoSugerencias && (
                      <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                  </div>
                  
                  <Form.Text className="text-muted">
                    {formData.tipo_mapeo === 'clave_sat' 
                      ? 'Escribe la clave o busca por descripción. Se mostrarán sugerencias automáticamente.'
                      : formData.tipo_mapeo === 'descripcion'
                        ? 'Escribe el texto que se buscará en la descripción de los conceptos. Se buscará de forma parcial (contiene el texto).'
                        : formData.tipo_mapeo === 'tipo_gasto'
                          ? 'Selecciona el tipo de comprobante del CFDI (I=Ingreso, E=Egreso, P=Pago, N=Nómina, T=Traslado).'
                          : formData.tipo_mapeo === 'uso_cfdi'
                            ? 'Selecciona el código de uso CFDI del receptor. Este mapeo se aplicará cuando el CFDI tenga este uso CFDI.'
                            : formData.tipo_mapeo === 'forma_pago'
                              ? 'Selecciona la forma de pago del CFDI. Este mapeo se aplicará cuando el comprobante tenga esta forma de pago.'
                              : formData.tipo_mapeo === 'metodo_pago'
                                ? 'Selecciona el método de pago del CFDI (PUE=una exhibición, PPD=parcialidades o diferido).'
                                : ''}
                  </Form.Text>
                  
                  {/* Información del Catálogo SAT */}
                  {formData.tipo_mapeo === 'clave_sat' && formData.valor_mapeo.trim() && !mostrarAutocompletar && (
                    <div className="mt-2">
                      {validatingCatalogo ? (
                        <div className="d-flex align-items-center">
                          <Spinner animation="border" size="sm" className="me-2" />
                          <small className="text-muted">Validando clave en Catálogo SAT...</small>
                        </div>
                      ) : catalogoInfo ? (
                        catalogoInfo.existe ? (
                          <Alert variant="success" className="py-2 mb-0">
                            <strong>✓ Clave válida</strong>
                            <br />
                            <small>{catalogoInfo.descripcion}</small>
                            {catalogoInfo.version && (
                              <>
                                <br />
                                <small className="text-muted">Versión: {catalogoInfo.version}</small>
                              </>
                            )}
                          </Alert>
                        ) : (
                          <Alert variant="warning" className="py-2 mb-0">
                            <strong>⚠ Clave no encontrada en el catálogo cargado</strong>
                            <br />
                            <small>La clave &quot;{catalogoInfo.clave}&quot; no está en el Catálogo SAT actual. Puede ser una clave nueva, de otra versión o no cargada. Puede continuar y crear el mapeo de todas formas.</small>
                          </Alert>
                        )
                      ) : null}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Prioridad</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="999"
                    value={formData.prioridad}
                    onChange={(e) => setFormData({ ...formData, prioridad: parseInt(e.target.value) || 100 })}
                  />
                  <Form.Text className="text-muted">
                    Menor número = mayor prioridad. Los mapeos se evalúan en orden de prioridad.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <Form.Check
                    type="switch"
                    label="Activo"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  />
                  <Form.Text className="text-muted">
                    Solo los mapeos activos se aplicarán automáticamente
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseForm}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                selectedMapping ? 'Actualizar' : 'Crear'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <AccountSearchModal
        show={showAccountSearchModal}
        onHide={() => setShowAccountSearchModal(false)}
        onSelect={handleAccountSearchSelect}
        clienteId={clienteContexto}
        pageSize={200}
      />

      {/* Modal de Confirmación de Eliminación */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de eliminar el mapeo para{' '}
          <strong>{mappingToDelete?.cliente_name || 'este cliente'}</strong>?
          <br />
          <small className="text-muted">
            Tipo: {mappingToDelete && getTipoMapeoLabel(mappingToDelete.tipo_mapeo)} - 
            Valor: {mappingToDelete?.valor_mapeo}
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              'Eliminar'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default AccountMapping
